import React, { useRef, useState, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import { Container, IconButton, Paper, Select, Switch, Tab, Tabs, Tooltip, Typography } from "@material-ui/core";
import FilterNoneOutlinedIcon from "@material-ui/icons/FilterNoneOutlined";
import TooltipContainer from "../../components/TooltipContainer/index.js";

import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n.js";
import api from "../../services/api";
import SocketSettings from "../../context/Socket/Listeners/SocketSettings/index.js";
import toastError from "../../errors/toastError";
import TabPanel from "../../components/TabPanel";

import DefaultFunctionsUtils from "../../utils/classes/DefaultFunctionsUtils.js";
import DefaultValuesUtils from "../../utils/classes/DefaultValuesUtils.js";

import { useGlobalRegisters } from "../../context/GlobalRegisters/GlobalRegistersContext.js";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		alignItems: "center",
		padding: theme.spacing(8, 8, 3),
		backgroundColor: theme.palette.background.default,
		color: theme.palette.text.primary,
	},

	paper: {
		padding: theme.spacing(2),
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		marginBottom: 12,
		"& .MuiOutlinedInput-notchedOutline": { borderColor: theme.palette.text.primary },
		"& .MuiSvgIcon-root": { color: theme.palette.text.primary },
	},

	settingOption: { marginLeft: "auto", },

	floatingButton: {
    transition: "transform 0.3s",
    "&:hover": { transform: "translateY(-5px)", },
  },
}));

const Settings = () => {
	// 	***************
	// 	** Variables **
	// 	***************
	const classes = useStyles();
	const history = useHistory();
	const { user } = useContext(AuthContext);
	const [tab, setTab] = useState(localStorage.getItem("tabOpenSettings"));
	const [tabOpen, setTabOpen] = useState(localStorage.getItem("tabOpenSettings"));
	const [settings, setSettings] = useState([]);
	const [isApiTokenCopied, setIsApiTokenCopied] = useState(false);
	
	const timeoutCopyRef = useRef(null);

	const { setShouldSyncRouteOnMenu } = useGlobalRegisters();



	// 	*****************
	// 	** Use Effects **
	// 	*****************
	useEffect(() => {
		if (user.profile === "user" || !user.configEnabled) {
			DefaultFunctionsUtils.redirectToTicketsPage(history);
			setShouldSyncRouteOnMenu(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	
	useEffect(() => {
		const fetchSession = async () => {
			try {
				const { data } = await api.get("/settings");
				setSettings(data);
			}
			catch (exception) {
				console.log("Settings Use Effect 1 Exception:", exception);
				toastError(exception);
			}
		};
		fetchSession();
	}, []);



	// 	***************
	// 	** Functions **
	// 	***************
	const handleChangeTabOpen = (event, newValue) => {
		setTabOpen(newValue);
		setTab(newValue);
		localStorage.setItem("tabOpenSettings", newValue);
	};

	const handleChangeSetting = async (event) => {
		const selectedValue = event.target.value;
		const settingKey = event.target.name;
		
		const shouldResetTabOpenTickets = selectedValue === "disabled"
			&& settingKey === "allowGroups"
			&& localStorage.getItem("tabOpenTickets") === "group";

		if (shouldResetTabOpenTickets) localStorage.setItem("tabOpenTickets", "open");

		try {
			await api.put(`/settings/${settingKey}`, { value: selectedValue });
			handleChangeTabOpen(event, tab);
		}
		catch (exception) {
			console.log("Handle Change Setting Exception:", exception);
			toastError(exception);
		}
	};

	const handleChangeSettingToggle = async (event) => {
		const settingKey = event.target.name;
		const selectedValue = event.target.checked ? "enabled" : "disabled";

		// ***---- Reseting Tab Open Tickets ----***
		const shouldResetByGroup = settingKey === "allowGroups" && selectedValue === "disabled" && localStorage.getItem("tabOpenTickets") === "group";
		const shouldResetByContactsView = settingKey === "allowContactsView" && selectedValue === "disabled" && localStorage.getItem("tabOpenTickets") === "contactsView";
		const shouldResetTabOpenTickets = shouldResetByGroup || shouldResetByContactsView;

		if (shouldResetTabOpenTickets) localStorage.setItem("tabOpenTickets", "open");

		// ***---- Reseting Merge Messages From Different Connections ----***
		const shouldResetMergeMessages = settingKey === "mergeMessagesFromDifferentConnectionsSetting" && selectedValue === "enabled";
		if (shouldResetMergeMessages) localStorage.setItem("mergeMessagesFromDifferentConnections", JSON.stringify([]));

		// ***---- API Request ----***
		try {
			await api.put(`/settings/${settingKey}`, { value: selectedValue });
			handleChangeTabOpen(event, tab);
		}
		catch (exception) {
			console.log("Handle Change Setting Toggle Exception:", exception);
			toastError(exception);
		}
	};

	const getSettingValue = (key) => {
		const { value } = settings.find(setting => setting.key === key);
		return value;
	};

	const handleCopyApiToken = async () => {
		setIsApiTokenCopied(true);
		await navigator.clipboard.writeText(getSettingValue("userApiToken"));
		
		if (timeoutCopyRef.current) clearTimeout(timeoutCopyRef.current);
		timeoutCopyRef.current = setTimeout(() => setIsApiTokenCopied(false), 500);
	};

	const handleConvertTimezoneValue = (value) => {
		if (value === DefaultValuesUtils.TIMEZONES["GMT-2"]) return "2";
		if (value === DefaultValuesUtils.TIMEZONES["GMT-3"]) return "3";
		if (value === DefaultValuesUtils.TIMEZONES["GMT-4"]) return "4";
		if (value === DefaultValuesUtils.TIMEZONES["GMT-5"]) return "5";
		return "3";
	};


	
	// 	************
	// 	** Return **
	// 	************
	return (
		<div className={classes.root}>
			<SocketSettings setSettings={setSettings} />

			<Container className={classes.container} maxWidth="sm">
				<Tabs 
					onChange={handleChangeTabOpen} 
					value={tabOpen}
					indicatorColor="primary"
					textColor="primary"
					variant="scrollable"
					scrollButtons="on"
					centered
				>
					<Tab label={i18n.t("settings.settings.general")} value={"general"}/>
					<Tab label={i18n.t("settings.settings.privacy")} value={"privacy"}/>
					<Tab label={i18n.t("settings.settings.schedule")} value={"schedule"}/>
					<Tab label={i18n.t("settings.settings.sendings")} value={"sendings"}/>
					<Tab label={i18n.t("settings.settings.service")} value={"service"}/>
				</Tabs>



				{/* 
					*****************
					** General Tab **
					*****************
				*/}
				<TabPanel value={tabOpen} name="general">
					<br /><hr /><br />

					{/* 
						*************************
						** Allow Default Queue **
						*************************
					*/}
					<Paper className={classes.paper}>
						<Typography variant="body1">
							{i18n.t("settings.settings.allowDefaultQueue.name")}
						</Typography>
						<Switch
        			checked={settings && settings.length > 0 && getSettingValue("allowDefaultQueue") === "enabled"}
        			onChange={async (event) => await handleChangeSettingToggle(event)}
        			name="allowDefaultQueue"
    				/>
					</Paper>



					{/* 
						******************
						** Allow Groups **
						******************
					*/}
					<Paper className={classes.paper}>
						<Typography variant="body1">
							{i18n.t("settings.settings.allowGroups.name")}
						</Typography>
						<Switch
        			checked={settings && settings.length > 0 && getSettingValue("allowGroups") === "enabled"}
        			onChange={async (event) => await handleChangeSettingToggle(event)}
        			name="allowGroups"
    				/>
					</Paper>



					{/* 
						*************************
						** Allow Contacts View **
						*************************
					*/}
					<Paper className={classes.paper}>
						<Typography variant="body1">
							{i18n.t("settings.settings.allowContactsView.name")}
							<TooltipContainer i18nMessage={"settings.settings.allowContactsView.tooltip"} />
						</Typography>

						<Switch
							checked={settings && settings.length > 0 && getSettingValue("allowContactsView") === "enabled"}
							onChange={async (event) => await handleChangeSettingToggle(event)}
							name="allowContactsView"
						/>
					</Paper>



					{/* 
						******************************************
						** Allow Contacts View To Type Of Users **
						******************************************
					*/}
					<Paper className={classes.paper}>
						<Typography variant="body1">
							{i18n.t("settings.settings.allowContactsViewToTypeOfUsers.name")}
						</Typography>
						<Select
							margin="dense"
							variant="outlined"
							native
							id="allowContactsViewToTypeOfUsers-setting"
							name="allowContactsViewTOTypeOfUsers"
							value={settings && settings.length > 0 && getSettingValue("allowContactsViewToTypeOfUsers")}
							className={classes.settingOption}
							onChange={async (event) => await handleChangeSetting(event)}
							disabled={settings && settings.length > 0 && getSettingValue("allowContactsView") === "disabled"}
						>
							<option value="admins">
								{i18n.t("settings.settings.allowContactsViewToTypeOfUsers.options.admins")}
							</option>
							<option value="all">
								{i18n.t("settings.settings.allowContactsViewToTypeOfUsers.options.all")}
							</option>
						</Select>
					</Paper>


					<br /><hr /><br />


					{/* 
						********************************
						** Automatically Reject Calls **
						********************************
					*/}
					<Paper className={classes.paper}>
						<Typography variant="body1">
							{i18n.t("settings.settings.automaticallyRejectCalls.name")}
							<TooltipContainer i18nMessage={"settings.settings.automaticallyRejectCalls.tooltip"} />
						</Typography>

						<Switch
							checked={settings && settings.length > 0 && getSettingValue("automaticallyRejectCalls") === "enabled"}
							onChange={async (event) => await handleChangeSettingToggle(event)}
							name={"automaticallyRejectCalls"}
						/>
					</Paper>



					{/* 
						*****************
						** Missed Call **
						*****************
					*/}
					<Paper className={classes.paper}>
						<Typography variant="body1">
							{i18n.t("settings.settings.missedCalls.name")}
						</Typography>
						<Switch
        			checked={settings && settings.length > 0 && getSettingValue("missedCalls") === "enabled"}
        			onChange={async (event) => await handleChangeSettingToggle(event)}
        			name="missedCalls"
    				/>
					</Paper>



					{/* 
						******************************
						** Tickets List Sort Method **
						******************************
					*/}
					<Paper className={classes.paper}>
						<Typography variant="body1">
							{i18n.t("settings.settings.ticketsListSortMethod.name")}
						</Typography>
						<Select
							margin="dense"
							variant="outlined"
							native
							id="ticketsListSortMethod-setting"
							name="ticketsListSortMethod"
							value={
								settings && settings.length > 0 && getSettingValue("ticketsListSortMethod")
							}
							className={classes.settingOption}
							onChange={async (event) => await handleChangeSetting(event)}
						>
							<option value="asc">
								{i18n.t("settings.settings.options.lessRecent")}
							</option>
							<option value="desc">
								{i18n.t("settings.settings.options.moreRecent")}
							</option>
						</Select>
					</Paper>



					{/*
						****************************
						** Ticket Dynamic Sorting **
						****************************
					*/}
					<Paper className={classes.paper}>
						<Typography variant="body1">
							{i18n.t("settings.settings.ticketsDynamicSorting.name")}
							<TooltipContainer i18nMessage={"settings.settings.ticketsDynamicSorting.tooltip"} />
						</Typography>

						<Switch
							checked={settings && settings.length > 0 && getSettingValue("ticketsDynamicSorting") === "enabled"}
							onChange={async (event) => await handleChangeSettingToggle(event)}
							name="ticketsDynamicSorting"
						/>
					</Paper>



					{/* 
						*******************
						** Enter is Send **
						*******************
					*/}
					<Paper className={classes.paper}>
						<Typography variant="body1">
							{i18n.t("settings.settings.enterIsSendMobile.name")}
						</Typography>
						<Switch
        			checked={settings && settings.length > 0 && getSettingValue("enterIsSendMobile") === "enabled"}
        			onChange={async (event) => await handleChangeSettingToggle(event)}
        			name="enterIsSendMobile"
    				/>
					</Paper>

					{/* 
						*****************************
						** Require Tag on Contacts **
						*****************************
					*/}
					<Paper className={classes.paper}>
						<Typography variant="body1">
							{i18n.t("settings.settings.requireTagOnContacts.name")}
						</Typography>
						<Switch
        			checked={settings && settings.length > 0 && getSettingValue("requireTagOnContactWhenClosingTicket") === "enabled"}
        			onChange={async (event) => await handleChangeSettingToggle(event)}
        			name="requireTagOnContactWhenClosingTicket"
    				/>
					</Paper>

					<br /><hr /><br />

					{/* 
						********************
						** User API Token **
						********************
					*/}
					<Paper className={classes.paper}>
						<Typography variant="body1">
							{i18n.t("settings.settings.apiToken.name")}
						</Typography>

						<Tooltip 
							title={
								isApiTokenCopied 
								? i18n.t("settings.settings.apiToken.copiedTooltip")
								: i18n.t("settings.settings.apiToken.copyTooltip")
							}
							placement="top-start" arrow
						>
							<IconButton
								color="inherit"
								className={classes.floatingButton}
								onClick={async () => await handleCopyApiToken()}
								disabled={settings && settings.length > 0 ? false : true}
							>
								<FilterNoneOutlinedIcon fontSize="small" />
							</IconButton>
						</Tooltip>
					</Paper>
				</TabPanel>



				{/* 
					*****************
					** Privacy Tab **
					*****************
				*/}
				<TabPanel value={tabOpen} name="privacy">
					<br /><hr /><br />

					{/* 
						***************************
						** Mark Messages as Read **
						***************************
					*/}
					<Paper className={classes.paper}>
						<Typography variant="body1">
							{i18n.t("settings.settings.markMessagesAsRead.name")}
						</Typography>
						<Switch
        			checked={settings && settings.length > 0 && getSettingValue("markMessagesAsRead") === "enabled"}
        			onChange={async (event) => await handleChangeSettingToggle(event)}
        			name="markMessagesAsRead"
    				/>
					</Paper>



					{/* 
						********************************
						** Admin Can View Other Chats **
						********************************
					*/}
					<Paper className={classes.paper}>
						<Typography variant="body1">
							{i18n.t("settings.settings.adminCanViewOtherChats.name")}
						</Typography>
						<Switch
        			checked={settings && settings.length > 0 && getSettingValue("adminCanViewOtherChats") === "enabled"}
        			onChange={async (event) => await handleChangeSettingToggle(event)}
        			name="adminCanViewOtherChats"
    				/>
					</Paper>



					{/* 
						***********************************
						** Show Presence Update on Chats **
						***********************************
					*/}
					<Paper className={classes.paper}>
						<Typography variant="body1">
							{i18n.t("settings.settings.viewPresenceUpdate.name")}
							<TooltipContainer i18nMessage={"settings.settings.viewPresenceUpdate.tooltip"} />
						</Typography>

						<Switch
							checked={settings && settings.length > 0 && getSettingValue("viewPresenceUpdate") === "enabled"}
							onChange={async (event) => await handleChangeSettingToggle(event)}
							name="viewPresenceUpdate"
						/>
					</Paper>



					<br /><hr /><br />

					{/* 
						********************************
						** Merge Connections Messages **
						********************************
					*/}
					<Paper className={classes.paper}>
						<Typography variant="body1">
							{i18n.t("settings.settings.mergeMessagesFromDifferentConnectionsSetting.name")}
						</Typography>
						<Switch
							checked={settings && settings.length > 0 && getSettingValue("mergeMessagesFromDifferentConnectionsSetting") === "enabled"}
							onChange={async (event) => await handleChangeSettingToggle(event)}
							name="mergeMessagesFromDifferentConnectionsSetting"
						/>
					</Paper>


					{/* 
						************************************************
						** Show Only Messages Related to Users" Chats **
						************************************************
					*/}
					<Paper className={classes.paper}>
						<Typography variant="body1">
							{i18n.t("settings.settings.showOnlyMessagesRelatedToUsersChats.name")}
							<TooltipContainer i18nMessage={"settings.settings.showOnlyMessagesRelatedToUsersChats.tooltip"} />
						</Typography>
						<Switch
							checked={settings && settings.length > 0 && getSettingValue("showOnlyMessagesRelatedToUsersChats") === "enabled"}
							onChange={async (event) => await handleChangeSettingToggle(event)}
							name="showOnlyMessagesRelatedToUsersChats"
						/>
					</Paper>



					{/* 
						******************************************
						** Show Messages Related To User Queues **
						******************************************
					*/}
					<Paper className={classes.paper}>
						<Typography variant="body1">
							{i18n.t("settings.settings.showMessagesRelatedToUserQueues.name")}
							<TooltipContainer i18nMessage={"settings.settings.showMessagesRelatedToUserQueues.tooltip"} />
						</Typography>

						<Switch
							checked={settings && settings.length > 0 && getSettingValue("showMessagesRelatedToUserQueues") === "enabled"}
							onChange={async (event) => await handleChangeSettingToggle(event)}
							name="showMessagesRelatedToUserQueues"
						/>
					</Paper>



					{/* 
						**************************************
						** Users Permissions by Connections **
						**************************************
					*/}
					<Paper className={classes.paper}>
						<Typography variant="body1">
							{i18n.t("settings.settings.userPermissionsByChip.name")}
						</Typography>
						<Switch
							checked={settings && settings.length > 0 && getSettingValue("userPermissionsByChip") === "enabled"}
							onChange={async (event) => await handleChangeSettingToggle(event)}
							name="userPermissionsByChip"
						/>
					</Paper>
				</TabPanel>



				{/* 
					******************
					** Schedule Tab **
					******************
				*/}
				<TabPanel value={tabOpen} name="schedule">
					<br /><hr /><br />

					{/* 
						**************
						** Timezone **
						**************
					*/}
					<Paper className={classes.paper}>
						<Typography variant="body1">
							{i18n.t("settings.settings.timezone.name")}
						</Typography>
						<Select
							margin="dense"
							variant="outlined"
							native
							id="timezone-setting"
							name="timezone"
							value={settings && settings.length > 0 && handleConvertTimezoneValue(getSettingValue("timezone"))}
							className={classes.settingOption}
							onChange={async (event) => await handleChangeSetting(event)}
						>
							<option value="2">GMT-2</option>
							<option value="3">GMT-3</option>
							<option value="4">GMT-4</option>
							<option value="5">GMT-5</option>
						</Select>
					</Paper>



					{/* 
						*******************
						** Awaiting Time **
						*******************
					*/}
					<Paper className={classes.paper}>
						<Typography variant="body1">
							{i18n.t("settings.settings.awaitingTime.name")}
						</Typography>

						<Select
							margin="dense"
							variant="outlined"
							native
							id="awaitingTime-setting"
							name="awaitingTime"
							value={
								settings && settings.length > 0 && getSettingValue("awaitingTime")
							}
							className={classes.settingOption}
							onChange={async (event) => await handleChangeSetting(event)}
						>
							<option value="-1">{i18n.t("settings.settings.options.never")}</option>
							<option value="10">10</option>
							<option value="15">15</option>
							<option value="20">20</option>
							<option value="25">25</option>
							<option value="30">30</option>
							<option value="45">45</option>
							<option value="60">60</option>
							<option value="90">90</option>
							<option value="120">120+</option>
						</Select>
					</Paper>



					{/* 
						*********************
						** Attendance Time **
						*********************
					*/}
					<Paper className={classes.paper}>
						<Typography variant="body1">
							{i18n.t("settings.settings.attendanceTime.name")}
						</Typography>

						<Select
							margin="dense"
							variant="outlined"
							native
							id="attendanceTime-setting"
							name="attendanceTime"
							value={
								settings && settings.length > 0 && getSettingValue("attendanceTime")
							}
							className={classes.settingOption}
							onChange={async (event) => await handleChangeSetting(event)}
						>
							<option value="-1">{i18n.t("settings.settings.options.never")}</option>
							<option value="10">10</option>
							<option value="15">15</option>
							<option value="20">20</option>
							<option value="25">25</option>
							<option value="30">30</option>
							<option value="45">45</option>
							<option value="60">60</option>
							<option value="90">90</option>
							<option value="120">120+</option>
						</Select>
					</Paper>

					<br /><hr /><br />

					{/* 
						*********************************************
						** Force Accept Long Awaiting Time Tickets **
						*********************************************
					*/}
					<Paper className={classes.paper}>
						<Typography variant="body1">
							{i18n.t("settings.settings.forceAcceptLongAwaitingTimeTickets.name")}
						</Typography>
						<Switch
        			checked={settings && settings.length > 0 && getSettingValue("forceAcceptLongAwaitingTimeTickets") === "enabled"}
        			onChange={async (event) => await handleChangeSettingToggle(event)}
        			name="forceAcceptLongAwaitingTimeTickets"
    				/>
					</Paper>

					{/* 
						*******************************
						** Force Accept First Ticket **
						*******************************
					*/}
					<Paper className={classes.paper}>
						<Typography variant="body1">
							{i18n.t("settings.settings.forceAcceptFirstTicket.name")}
						</Typography>
						<Switch
							checked={settings && settings.length > 0 && getSettingValue("forceAcceptFirstTicket") === "enabled"}
							onChange={async (event) => await handleChangeSettingToggle(event)}
							name="forceAcceptFirstTicket"
						/>
					</Paper>
				</TabPanel>



				{/* 
					******************
					** Sendings Tab **
					******************
				*/}
				<TabPanel value={tabOpen} name="sendings">
					<br /><hr /><br />

					{/* 
						************************
						** Send Notifications **
						************************
					*/}
					<Paper className={classes.paper}>
						<Typography variant="body1">
							{i18n.t("settings.settings.sendNotifications.name")}
						</Typography>
						<Select
							margin="dense"
							variant="outlined"
							native
							id="sendNotifications-setting"
							name="sendNotifications"
							value={
								settings && settings.length > 0 && getSettingValue("sendNotifications")
							}
							className={classes.settingOption}
							onChange={async (event) => await handleChangeSetting(event)}
						>
							<option value="open">
								{i18n.t("settings.settings.options.open")}
							</option>
							<option value="pending">
								{i18n.t("settings.settings.options.pending")}
							</option>
							<option value="both">
								{i18n.t("settings.settings.options.both")}
							</option>
						</Select>
					</Paper>



					{/* 
						*****************************************
						** Send Chats and Groups Notifications **
						*****************************************
					*/}
					<Paper className={classes.paper}>
						<Typography variant="body1">
							{i18n.t("settings.settings.sendNotificationsTo.name")}
						</Typography>
						<Select
							margin="dense"
							variant="outlined"
							native
							id="sendNotificationsChatsGroups-setting"
							name="sendNotificationsChatsGroups"
							value={
								settings && settings.length > 0 && getSettingValue("sendNotificationsChatsGroups")
							}
							className={classes.settingOption}
							onChange={async (event) => await handleChangeSetting(event)}
						>
							<option value="both">
								{i18n.t("settings.settings.options.both")}
							</option>
							<option value="chats">
								{i18n.t("settings.settings.options.chats")}
							</option>
							<option value="groups">
								{i18n.t("settings.settings.options.groups")}
							</option>
						</Select>
					</Paper>



					{/* 
						*******************************************
						** Sound Notification for Internal Chats **
						*******************************************
					*/}
					<Paper className={classes.paper}>
						<Typography variant="body1">
							{i18n.t("settings.settings.notificationSoundForInternalChats.name")}
						</Typography>
						<Switch
							checked={settings && settings.length > 0 && getSettingValue("notificationSoundForInternalChats") === "enabled"}
							onChange={async (event) => await handleChangeSettingToggle(event)}
							name="notificationSoundForInternalChats"
						/>
					</Paper>



					<br /><hr /><br />



					{/* 
						******************
						** Link Preview **
						******************
					*/}
					<Paper className={classes.paper}>
						<Typography variant="body1">
							{i18n.t("settings.settings.linkPreview.name")}
						</Typography>
						<Switch
        			checked={settings && settings.length > 0 && getSettingValue("linkPreview") === "enabled"}
        			onChange={async (event) => await handleChangeSettingToggle(event)}
        			name="linkPreview"
    				/>
					</Paper>



					{/* 
						***************************
						** Send Protocol Message **
						***************************
					*/}
					<Paper className={classes.paper}>
						<Typography variant="body1">
							{i18n.t("settings.settings.sendProtocolMessage.name")}
						</Typography>
						<Switch
        			checked={settings && settings.length > 0 && getSettingValue("sendProtocolMessage") === "enabled"}
        			onChange={async (event) => await handleChangeSettingToggle(event)}
        			name="sendProtocolMessage"
    				/>
					</Paper>



					{/*
						********************************
						** Send Signature on Messages **
						********************************
					*/}
					<Paper className={classes.paper}>
						<Typography variant="body1">
							{i18n.t("settings.settings.sendSignatureOnMessages.name")}
						</Typography>
						<Switch
        			checked={settings && settings.length > 0 && getSettingValue("sendSignatureOnMessages") === "enabled"}
        			onChange={async (event) => await handleChangeSettingToggle(event)}
        			name="sendSignatureOnMessages"
    				/>
					</Paper>



					{/* 
						*************************
						** Send Return Message **
						*************************
					*/}
					<Paper className={classes.paper}>
						<Typography variant="body1">
							{i18n.t("settings.settings.sendReturnMessage.name")}
						</Typography>
						<Switch
							checked={settings && settings.length > 0 && getSettingValue("sendReturnMessage") === "enabled"}
							onChange={async (event) => await handleChangeSettingToggle(event)}
							name="sendReturnMessage"
						/>
					</Paper>



					{/* 
						***************************
						** Send Transfer Message **
						***************************
					*/}
					<Paper className={classes.paper}>
						<Typography variant="body1">
							{i18n.t("settings.settings.sendTransferMessage.name")}
						</Typography>
						<Switch
        			checked={settings && settings.length > 0 && getSettingValue("sendTransferMessage") === "enabled"}
        			onChange={async (event) => await handleChangeSettingToggle(event)}
        			name="sendTransferMessage"
    				/>
					</Paper>



					{/* 
						****************************
						** Send Waiting Attendant **
						****************************
					*/}
					<Paper className={classes.paper}>
						<Typography variant="body1">
							{i18n.t("settings.settings.sendWaitingAttendant.name")}
						</Typography>
						<Switch
        			checked={settings && settings.length > 0 && getSettingValue("sendWaitingAttendant") === "enabled"}
        			onChange={async (event) => await handleChangeSettingToggle(event)}
        			name="sendWaitingAttendant"
    				/>
					</Paper>
				</TabPanel>



				{/* 
					*****************
					** Service Tab **
					*****************
				*/}
				<TabPanel value={tabOpen} name="service">
					<br /><hr /><br />

					{/* 
						*********************
						** Prioritize User **
						*********************
					*/}
					<Paper className={classes.paper}>
						<Typography variant="body1">
							{i18n.t("settings.settings.prioritizeUsers.name")}
						</Typography>
						<Switch
        			checked={settings && settings.length > 0 && getSettingValue("prioritizeUser") === "enabled"}
        			onChange={async (event) => await handleChangeSettingToggle(event)}
        			name="prioritizeUser"
    				/>
					</Paper>

					{/* 
						**************************
						** Prioritize User Time **
						**************************
					*/}
					<Paper className={classes.paper}>
						<Typography variant="body1">
							{i18n.t("settings.settings.prioritizeUsersTime.name")}
						</Typography>
						<Select
							margin="dense"
							variant="outlined"
							native
							id="prioritizeUserTime-setting"
							name="prioritizeUserTime"
							value={
								settings && settings.length > 0 && getSettingValue("prioritizeUserTime")
							}
							className={classes.settingOption}
							onChange={async (event) => await handleChangeSetting(event)}
						>
							<option value="1">1</option>
							<option value="2">2</option>
							<option value="3">3</option>
							<option value="4">4</option>
							<option value="5">5</option>
							<option value="10">10</option>
							{/* <option value="infinity">∞</option> */}
						</Select>
					</Paper>

					<br /><hr /><br />

					{/* 
						************************
						** Expiration Pending **
						************************
					*/}
					<Paper className={classes.paper}>
						<Typography variant="body1">
							{i18n.t("settings.settings.chatExpiration.name")}
						</Typography>
						<Select
							margin="dense"
							variant="outlined"
							native
							id="chatExpiration-setting"
							name="chatExpiration"
							value={
								settings && settings.length > 0 && getSettingValue("chatExpiration")
							}
							className={classes.settingOption}
							onChange={async (event) => await handleChangeSetting(event)}
						>
							<option value="0">{`${i18n.t("dashboard.messages.time.never")}`}</option>
							<option value="1440">{`24 ${i18n.t("dashboard.messages.time.hours")}`}</option>
							<option value="2880">{`48 ${i18n.t("dashboard.messages.time.hours")}`}</option>
						</Select>
					</Paper>

					{/* 
						********************************************
						** Send Rating Poll to Expiration Pending **
						********************************************
					*/}
					<Paper className={classes.paper}>
						<Typography variant="body1">
							{i18n.t("settings.settings.sendRatingPollOnChatExpiration.name")}
						</Typography>
						<Switch
							checked={settings && settings.length > 0 && getSettingValue("sendRatingPollOnChatExpiration") === "enabled"}
							onChange={async (event) => await handleChangeSettingToggle(event)}
							name="sendRatingPollOnChatExpiration"
						/>
					</Paper>

					{/* 
						***********************
						** Expiration Opened **
						***********************
					*/}
					<Paper className={classes.paper}>
						<Typography variant="body1">
							{i18n.t("settings.settings.chatExpirationOpened.name")}
						</Typography>
						<Select
							margin="dense"
							variant="outlined"
							native
							id="chatExpiration-setting"
							name="chatExpirationOpened"
							value={
								settings && settings.length > 0 && getSettingValue("chatExpirationOpened")
							}
							className={classes.settingOption}
							onChange={async (event) => await handleChangeSetting(event)}
						>
							<option value="0">{`${i18n.t("dashboard.messages.time.never")}`}</option>
							<option value="1440">{`24 ${i18n.t("dashboard.messages.time.hours")}`}</option>
							<option value="2880">{`48 ${i18n.t("dashboard.messages.time.hours")}`}</option>
						</Select>
					</Paper>

					{/* 
						*******************************************
						** Send Rating Poll to Expiration Opened **
						*******************************************
					*/}
					<Paper className={classes.paper}>
						<Typography variant="body1">
						{i18n.t("settings.settings.sendRatingPollOnChatExpirationOpened.name")}
						</Typography>
						<Switch
							checked={settings && settings.length > 0 && getSettingValue("sendRatingPollOnChatExpirationOpened") === "enabled"}
							onChange={async (event) => await handleChangeSettingToggle(event)}
							name="sendRatingPollOnChatExpirationOpened"
						/>
					</Paper>

					<br /><hr /><br />

					{/* 
						*********************************************
						** Allow Transfer Tickets to Offline Users **
						*********************************************
					*/}
					<Paper className={classes.paper}>
						<Typography variant="body1">
							{i18n.t("settings.settings.allowTransferTicketsToOfflineUsers.name")}
						</Typography>
						<Switch
        			checked={settings && settings.length > 0 && getSettingValue("allowTransferTicketsToOfflineUsers") === "enabled"}
        			onChange={async (event) => await handleChangeSettingToggle(event)}
        			name="allowTransferTicketsToOfflineUsers"
    				/>
					</Paper>

					{/* 
						***************************************************
						** Allow Resolve Ticket Without Good Bye Message **
						***************************************************
					*/}
					<Paper className={classes.paper}>
						<Typography variant="body1">
							{i18n.t("settings.settings.allowResolveTicketWithoutGoodBye.name")}
						</Typography>
						<Switch
							checked={settings && settings.length > 0 && getSettingValue("allowResolveTicketWithoutGoodBye") === "enabled"}
							onChange={async (event) => await handleChangeSettingToggle(event)}
							name="allowResolveTicketWithoutGoodBye"
						/>
					</Paper>

					{/* 
						***************************************
						** Admins Control to Pending Tickets **
						***************************************
					*/}
					<Paper className={classes.paper}>
						<Typography variant="body1">
							{i18n.t("settings.settings.adminsPendingTicketsControl.name")}
						</Typography>
						<Switch
							checked={settings && settings.length > 0 && getSettingValue("adminsPendingTicketsControl") === "enabled"}
							onChange={async (event) => await handleChangeSettingToggle(event)}
							name="adminsPendingTicketsControl"
						/>
					</Paper>
				</TabPanel>
			</Container>
		</div>
	);
};

export default Settings;
